import { useQuery, useMutation } from "@apollo/client/react";
import { useQueryClient } from "@tanstack/react-query";
import { processEntityDataFromApi } from "@/lib/utils/crm/task-data-processing";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import {
  GetCompaniessDocument,
  GetCompanyDocument,
  CreateCompanyDocument,
  UpdateCompanyDocument,
  DeleteCompanyDocument,
  GetCompaniessQuery,
  GetCompanyQuery,
  CreateCompanyMutation,
  UpdateCompanyMutation,
  DeleteCompanyMutation,
  CompaniesInsertInput,
  Companies,
} from "@/src/gql/graphql";
import { ENHANCED_COMPANIES_CONFIG } from "@/features/companies/components/EnhancedCompaniesConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import type { CompanyEntity } from "@/lib/types/company";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
// Company writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

function parseCompanyFromApi(node: Companies): CompanyEntity {
  return processEntityDataFromApi(node, ENHANCED_COMPANIES_CONFIG.fields);
}

// ============================================================================
// COMPANIES HOOKS
// ============================================================================

export const useCompanyHooks = (): EntityHooks<CompanyEntity> =>
  ({
    queryDocument: GetCompaniessDocument,
    usePaginated: (params?: Record<string, unknown>) =>
      // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
      usePaginatedCollection(
        GetCompaniessDocument,
        params,
        (data: GetCompaniessQuery) => data.companiesCollection,
        (node) => parseCompanyFromApi(node as Companies)
      ),
    useInfinite: (_params?: unknown) => {
      return {
        data: undefined,
        isLoading: false,
        error: null,
        refetch: () => {},
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      };
    },
    useSingle: (id: string, options?: { enabled?: boolean }) => {
      const result = useQuery<GetCompanyQuery>(GetCompanyDocument, {
        variables: { id },
        errorPolicy: "all",
        skip: !id || options?.enabled === false,
      });
      return {
        ...result,
        data: (result.data?.companiesCollection?.edges?.[0]?.node
          ? parseCompanyFromApi(result.data.companiesCollection.edges[0].node as Companies)
          : null) as CompanyEntity | undefined,
        isLoading: result.loading,
        error: (result.error as unknown as Error) ?? null,
        refetch: result.refetch,
      };
    },

    useUpdate: () => {
      const [mutate, result] = useMutation<UpdateCompanyMutation>(UpdateCompanyDocument);

      return {
        mutateAsync: async ({ id, ...input }: { id: string } & Partial<Companies>) => {
          const cleanInput = prepareEntityGraphQlMutationInput(
            { ...input },
            ENHANCED_COMPANIES_CONFIG.fields,
            { mode: "update" }
          );

          const { data, error } = await mutate({
            variables: { id, input: cleanInput },
            errorPolicy: "all",
          });

          if (error) throw crmMutationError(error, "Couldn't update the company.");

          const updated = data?.updatecompaniesCollection?.records?.[0];
          if (!updated) throw new Error("Couldn't update the company.");
          return parseCompanyFromApi(updated as Companies);
        },
        isPending: result.loading,
      };
    },

    useDelete: () => {
      const [mutate, result] = useMutation<DeleteCompanyMutation>(DeleteCompanyDocument);

      return {
        mutateAsync: async (id: string) => {
          const { data, error } = await mutate({
            variables: { id },
            errorPolicy: "all",
          });

          if (error) throw crmMutationError(error, "Couldn't delete the company.");
          const deletedId = data?.deleteFromcompaniesCollection?.records?.[0]?.id ?? id;
          if (!deletedId) throw new Error("Couldn't delete the company.");
          return deletedId;
        },
        isPending: result.loading,
      };
    },
    useCreate: () => {
      const [mutate, result] = useMutation<CreateCompanyMutation>(CreateCompanyDocument);
      return {
        mutateAsync: async (input: CompaniesInsertInput) => {
          const cleanInput = prepareEntityGraphQlMutationInput(
            { ...input },
            ENHANCED_COMPANIES_CONFIG.fields,
            { mode: "insert" }
          );
          const { data, error } = await mutate({
            variables: { input: cleanInput },
            errorPolicy: "all",
          });
          if (error) throw crmMutationError(error, "Couldn't create the company.");
          const created = data?.insertIntocompaniesCollection?.records?.[0];
          if (!created) throw new Error("Couldn't create the company.");
          return created as CompanyEntity;
        },
        isPending: result.loading,
      };
    },

    getFieldConfig: (fieldName: string) => {
      return (
        ENHANCED_COMPANIES_CONFIG.fields[
          fieldName
        ] || null
      );
    },

    getTableConfig: () => {
      return ENHANCED_COMPANIES_CONFIG;
    },
  });
